import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import VirtualList from 'vue-virtual-scroll-list'
import { first, compact, includes, isEmpty, isArray, isNil, flatten } from 'lodash'

import types from '../../store/globaltypes'
import planningitem from '../../components/planningitem'
import cultivationitem from '../../components/cultivationitem'
import infoline from '../../components/infoline'
import { getAllLinkedProductIdsForAction, getCropMethodEndDate } from '../../helpers/crops'

function createPlanningItemViewModel (moment, crop, item) {
  const action = crop[`method${item.methodID}action${item.stepID}_action_type`]
  const video = crop[`method${item.methodID}action${item.stepID}_video`]
  const method = crop[`method${item.methodID}_methodname`]
  const tips = crop[`method${item.methodID}action${item.stepID}_tips`]
  const location = crop[`method${item.methodID}action${item.stepID}_location`]
  const formattedDate = item.period.format('DD-MM-YY')
  const title = `${formattedDate}: ${action}`
  const sendNotifications = isNil(item.sendNotifications) || item.sendNotifications

  const productIDs = getAllLinkedProductIdsForAction(crop, item.methodID, item.stepID)

  return {
    ...item,
    action,
    title,
    crop: crop.post_title,
    method,
    tips,
    location,
    cropNavParam: crop.post_title.toLowerCase(),
    image: crop.image,
    hasNotes: !!item.notes,
    video,
    productIDs,
    formattedDate,
    sendNotifications,
    date: moment.isMoment(item.period) ? item.period.format('L') : '--'
  }
}

function createCultivationItemViewModel (moment, crop, items) {
  const item = items[0]
  const formattedDate = item.period.format('DD-MM-YY')
  const method = crop[`method${item.methodID}_methodname`]
  const title = `${formattedDate}: ${crop.post_title} / ${method}`
  const productIDs = flatten(items.map(x => getAllLinkedProductIdsForAction(crop, x.methodID, x.stepID)))
  const numTasks = items.length
  const numCompletedTasks = items.filter(x => x.done).length

  return {
    ...item,
    method,
    crop: crop.post_title,
    continuedCrops: crop.continued_crops || [],
    cropNavParam: crop.post_title.toLowerCase(),
    image: crop.image,
    title,
    productIDs,
    numTasks,
    numCompletedTasks
  }
}

export default {
  name: 'calendarview',
  components: { infoline, planningitem, 'virtual-list': VirtualList },
  props: [],
  data () {
    return {
      planningItemComponent: planningitem,
      cultivationItemComponent: cultivationitem,
      cropGroupFilter: null,
      filter: 'all',
      pastVisible: false,
      itemToDelete: null,
      itemForDetails: null,
      itemForDetailsNotes: null,
      itemForVideo: null,
      itemForProducts: null,
      showVideo: false,
      showDatePicker: false,
      showProducts: false,
      itemForDetailsNewDate: null,
      itemForDetailsSendNotifications: null
    }
  },
  computed: {
    ...mapState({
      isLoading: state => !state.ui.calendar.isDataLoaded,
      isLoadingMoreDataPast: state => state.ui.calendar.isLoadingMoreDataPast,
      isLoadingMoreDataFuture: state => state.ui.calendar.isLoadingMoreDataFuture,
      selectedDate: state => state.ui.calendar.selectedDate,
      cropGroupFilterState: state => state.crops.filter
    }),
    ...mapGetters({
      getItemById: types.planning.getters.byId,
      isSomePastDataLoaded: types.planning.getters.isSomePastDataLoaded,
      cultivations: types.planning.getters.cultivations,
      cropByName: types.crops.getters.crop,
      cropGroups: types.crops.getters.cropgroups,
      itemsByCultivation: types.planning.getters.byCultivation,
      all: types.planning.getters.all,
      past: types.planning.getters.past,
      coming: types.planning.getters.coming,
      comingWeek: types.planning.getters.comingWeek,
      forgotten: types.planning.getters.forgotten,
      forDate: types.planning.getters.forDate,
      translatedCropById: types.crops.getters.translatedCropById,
      isAllDataLoaded: types.planning.getters.isAllDataLoaded,
      locale: types.user.getters.localeCode,
      groupedProducts: types.products.getters.groupByCategory,
      areProductsLoaded: types.products.getters.areProductsLoaded
    }),
    filters () {
      return [ this.$t('crops.all_crops'), ...this.cropGroups ]
    },
    itemComponent () {
      return this.filter === 'cultivations' ? this.cultivationItemComponent : this.planningItemComponent
    },
    isLoadingProducts () {
      return !this.areProductsLoaded(this.itemForProducts.productIDs)
    },
    linkedProds () {
      if (!this.itemForProducts || !this.itemForProducts.crop) return []
      return this.groupedProducts(this.itemForProducts.productIDs)
    },
    areAllLinkedProductsMissing () {
      return isEmpty(this.linkedProds)
    },
    showDeletionPrompt: {
      get () {
        return this.itemToDelete !== null
      },
      set (val) {
        if (!val) this.itemToDelete = null
      }
    },
    showItemDetails: {
      get () {
        return this.itemForDetails !== null
      },
      set (val) {
        if (!val) this.itemForDetails = null
      }
    },
    showItemInfo: {
      get () {
        return this.itemForInfo !== null
      },
      set (val) {
        if (!val) this.itemForInfo = null
      }
    },
    isLoadingMoreData () {
      if (this.filter === 'past' || this.filter === 'forgotten') return this.isLoadingMoreDataPast
      return this.isLoadingMoreDataFuture
    },
    selection () {
      if (this.selectedDate) return Object.freeze(this.forDate(this.selectedDate))
      switch (this.filter) {
        case 'past': return Object.freeze(this.past)
        case 'cultivations': return Object.freeze(this.cultivations)
        default: return Object.freeze(this.coming)
      }
    },
    selectionFast () {
      const cropGroupFilter = this.cropGroupFilterState
      return compact(this.selection
        .map(item => {
          const cropID = isArray(item) ? item[0].cropID : item.cropID
          const crop = this.translatedCropById(cropID)
          if (cropGroupFilter && !includes(crop.crop_group, cropGroupFilter)) return undefined
          return this.filter === 'cultivations'
            ? createCultivationItemViewModel(this.moment, crop, item)
            : createPlanningItemViewModel(this.moment, crop, item)
        }))
    },
    filterTab: {
      get () {
        if (this.selectedDate) return 3
        switch (this.filter) {
          case 'past': return 1
          case 'cultivations': return 2
        }
        return 0
      },
      set (val) {
        this.setSelectedDate(null)
        switch (val) {
          case 0:
            this.filter = 'all'
            break
          case 1:
            this.filter = 'past'
            break
          case 2:
            this.filter = 'cultivations'
            break
        }
      }
    },
    filterModel () {
      return this.selectedDate ? 'date' : this.filter
    },
    pastFast () {
      return this.past.map(item => {
        const crop = this.translatedCropById(item.cropID)
        return createPlanningItemViewModel(this.moment, crop, item)
      })
    },
    numVisibleItems () {
      return 4 + Math.ceil((window.innerHeight) / 72)
    },
    virtualListExtraProps () {
      return {
        onCheck: this.onItemCheck,
        onClick: this.onItemClick,
        onClickInfo: this.onItemClickInfo,
        onClickDelete: this.onItemDelete,
        onClickVideo: this.onItemClickVideo,
        onClickProducts: this.onItemClickProducts,
        onClickContinuedCrops: this.onItemClickContinuedCrops
      }
    },
    itemForDetailsLocation () {
      if (!this.itemForDetails) return ''
      switch (this.itemForDetails.location) {
        case 'indoor': return this.$t('crops.indoor')
        case 'outdoor': return this.$t('crops.outdoor')
        case 'under glass': return this.$t('crops.under_glass')
        case 'propagator': return this.$t('crops.propagator')
      }
    }
  },
  methods: {
    ...mapActions({
      loadMore: types.planning.actions.load,
      loadMorePast: types.planning.actions.loadPast,
      scheduleNotifications: types.planning.actions.scheduleNotifications,
      showInfo: types.main.actions.setInformation,
      addPlanningItem: types.planning.actions.add,
      deletePlanningItem: types.planning.actions.deleteItem,
      deleteCultivation: types.planning.actions.deleteCultivation,
      loadProducts: types.products.actions.loadList,
      addToCart: types.shoppingcart.actions.addItemToCart,
      filterCrops: types.crops.actions.filter
    }),
    ...mapMutations({
      setSelectedDate: types.ui.mutations.setSelectedDate
    }),
    onLoadMoreClick () {
      if (this.filter === 'past' || this.filter === 'forgotten') this.loadMorePast()
      else this.loadMore()
    },
    onItemCheck (item) {
      if (!item.done) {
        this.$ga.event({
          eventCategory: 'Planning',
          eventAction: 'Task done'
        })
        this.showInfo(this.$i18n.t('calendar.completed_action'))
        item.done = true
      } else {
        this.$ga.event({
          eventCategory: 'Planning',
          eventAction: 'Task undone'
        })
        this.showInfo(this.$i18n.t('calendar.cancel_completed_action'))
        item.done = false
      }
      this.addPlanningItem(item)
    },
    onItemClick (item) {
      this.itemForDetails = item
      this.itemForDetailsNotes = item.notes
      this.showDatePicker = false
    },
    // -- delete prompt
    onItemDelete (item) {
      this.itemToDelete = item
    },
    onItemDeleteCancel () {
      this.itemToDelete = null
    },
    onItemDeleteConfirmItem () {
      this.deletePlanningItem(this.itemToDelete)
      this.showInfo(this.$i18n.t('calendar.removed_action'))
      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Task deleted'
      })
      this.itemToDelete = null
    },
    onItemDeleteConfirmCultivation () {
      this.deleteCultivation(this.itemToDelete)
      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Crop deleted'
      })
      this.itemToDelete = null
    },
    // -- details
    onItemDetailsClose (ev) {
      this.itemForDetails = null
    },
    onItemDetailsToggleNotifications (ev) {
      let item = this.getItemById(this.itemForDetails.key)
      this.itemForDetails.sendNotifications = !this.itemForDetails.sendNotifications
      item.sendNotifications = this.itemForDetails.sendNotifications
      this.addPlanningItem(item)

      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Task notifications turned ' + (this.itemForDetails.sendNotifications ? 'on' : 'off')
      })
      this.scheduleNotifications()
    },
    onItemDetailsPickNewDate (ev) {
      this.itemForDetailsNewDate = this.itemForDetails.period.format('YYYY-MM-DD')
      this.showDatePicker = true
    },
    onItemsDetailConfirmNewDate (ev) {
      this.itemForDetails.formattedDate = this.moment(this.itemForDetailsNewDate).format('DD-MM-YY')
      let item = this.getItemById(this.itemForDetails.key)
      item.period = this.itemForDetailsNewDate
      this.itemForDetailsNewDate = null
      this.addPlanningItem(item)
      this.showDatePicker = false

      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Task rescheduled'
      })
      this.scheduleNotifications()
    },
    onItemDetailsBlur (ev) {
      let item = this.getItemById(this.itemForDetails.key)
      item.notes = this.itemForDetailsNotes
      this.addPlanningItem(item)
    },
    // -- video
    onItemClickVideo (item) {
      this.itemForVideo = item
      this.showVideo = true

      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Video shown'
      })
    },
    onVideoClose () {
      this.showVideo = false
    },
    // -- products
    onItemClickProducts (item) {
      this.itemForProducts = item
      this.showProducts = true
      this.loadProducts(this.itemForProducts.productIDs)

      this.$ga.event({
        eventCategory: 'Planning',
        eventAction: 'Products shown'
      })
    },
    onProductsClose () {
      this.showProducts = false
    },
    addProductToCart (productID, variationID) {
      this.addToCart({
        productID,
        cropID: this.itemForProducts.cropID,
        variationID
      })
      this.$ga.event({
        eventCategory: 'Product',
        eventAction: 'Product added to wishlist',
        eventLabel: `Added from page ${this.$route.name}`
      })
    },
    // --
    onItemClickContinuedCrops (item) {
      const orderedDates = this.itemsByCultivation(item.ID)
        .sort((lhs, rhs) => lhs.stepID < rhs.stepID ? -1 : 1)
        .map(x => this.moment(x.period))
      const startDate = first(orderedDates)
      const endDate = getCropMethodEndDate(this.cropByName(item.crop), 1, startDate)
      const query = {
        continuedcrops: item.crop.toLowerCase(),
        from: endDate.format('YYYY-MM-DD')
      }
      this.$router.push({ name: 'wizard', query })
    },
    toSettings () {
      this.$router.push({ name: 'settings' })
    }
  },
  watch: {
    $route (to) {
      if (to.name === 'calendar') {
        this.cropGroupFilter = this.cropGroupFilterState
      }
    },
    pastVisible (val) {
      if (val && !this.isSomePastDataLoaded) this.loadMorePast()
    },
    filter (val) {
      const isPastFilter = val === 'past' || val === 'forgotten'
      if (isPastFilter && !this.isSomePastDataLoaded) this.loadMorePast()
    },
    cropGroupFilter (val) {
      let index = this.cropGroups.indexOf(val)
      if (index === -1) this.filterCrops(null)
      else this.filterCrops(val)
    },
    selectionFast () {
      if (this.$refs.list) {
        const offset = this.$refs.list.getOffset()
        this.$nextTick(() => {
          if (this.$refs.list) {
            this.$refs.list.scrollToOffset(offset + 50)
            this.$refs.list.scrollToOffset(offset)
          }
        })
      }
    }
  }
}
